import { useEffect, useRef } from 'react';
import { useAccessibility } from '@/contexts/AccessibilityContext';

/**
 * Phase label mapping. Used by every breathing modal — we normalise the raw
 * phase keys to a human-friendly French imperative so screen readers can
 * announce the exercise step.
 */
const PHASE_LABELS = {
  inhale: 'Inspirez',
  exhale: 'Expirez',
  hold: 'Retenez',
  holdIn: 'Retenez',
  holdOut: 'Pausez',
  rest: 'Pausez',
  ready: '',
  still: '',
};

const pickFrenchVoice = () => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices?.length) return null;
  // Prefer a female natural French voice if available
  const fr = voices.filter(v => /fr/i.test(v.lang));
  if (!fr.length) return voices[0];
  const preferred = fr.find(v => /thomas|amelie|marie|audrey|julie/i.test(v.name));
  return preferred || fr[0];
};

/**
 * Hook: speak the current breathing phase whenever it changes, as long as
 * voiceGuidance is enabled in Accessibility preferences AND the exercise
 * is active (so we don't talk while paused / in the ready screen).
 *
 * We debounce back-to-back utterances via `speechSynthesis.cancel()` so
 * fast phase changes don't queue up a long backlog.
 */
export function useVoiceGuidance(phase, active, { customLabel } = {}) {
  const { voiceGuidance } = useAccessibility();
  const lastSpokenRef = useRef(null);

  useEffect(() => {
    if (!voiceGuidance || !active) {
      lastSpokenRef.current = null;
      return;
    }
    if (typeof window === 'undefined' || !window.speechSynthesis) return;

    const label = customLabel?.[phase] ?? PHASE_LABELS[phase] ?? '';
    if (!label) return;
    if (lastSpokenRef.current === label) return;
    lastSpokenRef.current = label;

    try {
      window.speechSynthesis.cancel();
      const u = new window.SpeechSynthesisUtterance(label);
      u.lang = 'fr-FR';
      u.rate = 0.95;
      u.pitch = 1.0;
      u.volume = 1.0;
      const voice = pickFrenchVoice();
      if (voice) u.voice = voice;
      window.speechSynthesis.speak(u);
    } catch (_err) {
      // Any TTS failure is non-critical.
    }
  }, [phase, active, voiceGuidance, customLabel]);

  // Stop talking when leaving the exercise
  useEffect(() => {
    return () => {
      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    };
  }, []);
}
